/**
 * One person, in a side panel (`?user=<id>`): status, workbench role, when they
 * were last active, the projects they are in (name and role only, not links:
 * an admin sees no project data from here), and Deactivate / Reactivate.
 * Deactivate is confirmed first; turning off yourself or an admin is disabled,
 * with the reason beside it.
 */

import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { SidePanel } from "@/components/ui/overlay";
import { Pill } from "@/components/ui/pill";
import { SectionLabel } from "@/components/ui/box";
import { ErrorState, Skeleton, errorText } from "@/components/ui/states";
import { useCurrentUser } from "@/features/auth/hooks";
import { ROLE_LABEL } from "@/features/projects/roles";
import { formatDate, formatRelative } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";
import { DeactivateDialog } from "./deactivate-dialog";
import { useSetUserActive, useUser, useUserAdminAccess } from "./hooks";
import { statusOf, type UserDetail } from "./types";
import { blockedReason } from "./user-access";
import { StatusLabel } from "./user-status";

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[130px_1fr] items-baseline gap-3 text-[13px]">
      <dt className="text-muted">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Projects({ user }: { user: UserDetail }) {
  return (
    <section className="flex flex-col gap-2" aria-label="Projects">
      <SectionLabel>Projects ({user.projects.length})</SectionLabel>
      {user.projects.length === 0 ? (
        <p className="text-[13px] text-muted">Not in any project yet.</p>
      ) : (
        <ul className="divide-y divide-line-soft rounded-lg border border-line">
          {user.projects.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-2 px-3.5 py-2.5 text-[13px]">
              <span className="min-w-0 flex-1 truncate font-medium">{p.name}</span>
              {p.only_active_owner && <Pill tone="warn">Only active owner</Pill>}
              <Pill>{ROLE_LABEL[p.role]}</Pill>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Actions({ user }: { user: UserDetail }) {
  const me = useCurrentUser().data?.email;
  const notify = useUiStore((s) => s.notify);
  const setActive = useSetUserActive();
  const [confirming, setConfirming] = useState(false);
  const active = statusOf(user) === "active";
  const reason = active ? blockedReason(user, me) : null;
  const who = user.name || user.email;
  const run = (next: boolean) =>
    setActive.mutate(
      { id: user.id, active: next },
      {
        onSuccess: () => {
          setConfirming(false);
          notify(next ? `Reactivated ${who}.` : `Deactivated ${who}.`);
        },
        onError: (e) => notify(errorText(e, "Could not change their access. Try again."), "error"),
      },
    );
  return (
    <>
      <p id="user-action-note" className="flex-1 text-[12px] leading-snug text-muted">
        {reason ??
          (active
            ? "Signs them out on their next click. Nothing is deleted."
            : "They can sign in again and see their projects as before.")}
      </p>
      {active ? (
        <Button
          onClick={() => setConfirming(true)}
          disabled={reason !== null}
          title={reason ?? undefined}
          aria-describedby="user-action-note"
          className="text-err"
        >
          Deactivate
        </Button>
      ) : (
        <Button variant="primary" onClick={() => run(true)} disabled={setActive.isPending}>
          {setActive.isPending ? "Reactivating…" : "Reactivate"}
        </Button>
      )}
      {confirming && (
        <DeactivateDialog
          user={user}
          pending={setActive.isPending}
          onConfirm={() => run(false)}
          onClose={() => setConfirming(false)}
        />
      )}
    </>
  );
}

export function UserPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const query = useUser(id);
  const { canManage } = useUserAdminAccess();
  const me = useCurrentUser().data?.email;
  const user = query.data;
  return (
    <SidePanel
      width={480}
      title={user ? user.name || user.email : "Person"}
      subtitle={user?.name ? user.email : undefined}
      onClose={onClose}
      footer={user && canManage ? <Actions user={user} /> : undefined}
    >
      {query.isPending && <Skeleton rows={5} label="Loading person" />}
      {query.isError && <ErrorState error={query.error} what="this person" />}
      {user && (
        <>
          <dl className="flex flex-col gap-2.5">
            <Row label="Status">
              <StatusLabel user={user} />
            </Row>
            <Row label="Workbench role">
              {user.is_admin ? <Pill tone="gold">Admin</Pill> : "User"}
              {user.email === me && <span className="ml-2 text-muted">(you)</span>}
            </Row>
            <Row label="Last active">{user.last_seen_at ? formatRelative(user.last_seen_at) : "Never"}</Row>
            <Row label="First signed in">{formatDate(user.created_at)}</Row>
            {user.deactivated_at && <Row label="Deactivated">{formatDate(user.deactivated_at)}</Row>}
          </dl>
          <Projects user={user} />
        </>
      )}
    </SidePanel>
  );
}
